<template>
  <div id="booking">
    <Loading v-if="loading || (loadingSlots && step === 3)" />
    <div class="content">
      <div class="container">
        <div class="row align-items-center">
          <div class="col d-flex flex-column align-items-center">
            <div class="blue-card" style="position: relative">
              <!-- Heading -->
              <div v-if="step !== 5">
                <h2>{{ apidata.system.booking_title }}</h2>
                <h3>{{ stepNames[step] }}</h3>
                <div class="step-icons">
                  <div
                    v-for="stepNumber in createTicket ? [3, 4] : [1, 2, 3, 4]"
                    :key="stepNumber"
                    @click="selectStep(stepNumber)"
                    :class="{ 'step-clickable': stepNumber <= step }"
                  >
                    <div
                      v-if="stepNumber >= step"
                      :class="{
                        'step-number': true,
                        current: stepNumber === step,
                      }"
                    >
                      {{ createTicket ? stepNumber - 2 : stepNumber }}
                    </div>
                    <div v-else class="step-tick">
                      <span class="material-icons">done</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Step 1: Locate details -->
              <div v-if="step === 1" id="step-1">
                <FormulateForm @submit="getTicket">
                  <div class="ticket-id-container">
                    <FormulateInput
                      type="number"
                      v-model="ticket_id"
                      :label="apidata.system.booking_ticket_id_label"
                      placeholder="e.g. 123456"
                      validation="required|number"
                      error-behavior="submit"
                      style="flex-grow: 1"
                    />
                    <VTooltip :triggers="['hover']" :autoHide="true">
                      <div class="ticket-id-question">
                        <span class="material-icons"> question_mark </span>
                      </div>
                      <template #popper>
                        <div
                          class="ticket-id-tooltip"
                          v-if="apidata.system.system_id === 'demo_1'"
                        >
                          If you do not have a
                          {{ apidata.system.booking_ticket_id_label }}, please
                          contact your Superior representative.
                        </div>
                        <div class="ticket-id-tooltip" v-else>
                          Your {{ apidata.system.booking_ticket_id_label }} can
                          be found in the appointment request email recently
                          sent to you. If you cannot locate your ID, please call
                          {{ apidata.system.booking_phone }}
                        </div>
                      </template>
                    </VTooltip>
                  </div>
                  <FormulateInput type="submit" name="Go to Step 2" />
                  <FormulateInput :style="apidata.system.inputstyle" @click="triggerCreateTicketMode" type="button" :name="`I don't have a ${apidata.system.booking_ticket_id_label}`" />
                </FormulateForm>
              </div>
              <!-- Step 2: Confirm your details -->
              <div v-if="step === 2" id="step-2">
                <p>
                  Based on your {{ apidata.system.booking_ticket_id_label }}, we
                  believe you are one of the following people. Please select who
                  will be attending:
                </p>
                <div class="cards" v-if="ticket_data">
                  <div
                    class="card"
                    :class="{ active: selectedContactId === contact.id }"
                    v-for="contact in ticket_data.contacts"
                    :key="contact.id"
                    @click="selectContact(contact)"
                  >
                    <h3 style="font-size: 20px; margin: 0; line-height: 100%">
                      {{ contact.properties.firstname }}
                      {{ contact.properties.lastname }}
                    </h3>
                    <p style="margin-top: 8px">
                      {{ contact.properties.company }}
                    </p>
                  </div>
                  <div
                    class="card"
                    @click="selectNewContact"
                    :class="{ active: selectedContactId === 'none' }"
                  >
                    <p>Someone else from {{ ticket_data.company.name }}</p>
                  </div>
                </div>
                <FormulateForm @submit="completeStep2" style="margin-top: 30px">
                  <FormulateInput type="submit" name="Go to Step 3" />
                </FormulateForm>
              </div>
              <!-- Step 3: Schedule appointment -->
              <div v-if="step === 3" id="step-3">
                <div class="show-booked-error" v-if="showBookedError">
                  Unfortunately, a booking was just made for this time slot and
                  rep. Please try again.
                </div>
                <div class="timezone-selector" style="margin-bottom: 20px">
                  <h3 style="font-size: 20px">Select timezone</h3>
                  <div style="position: relative">
                    <span class="drop-down-arrow"
                      ><span class="material-icons"> public </span></span
                    >
                    <FormulateInput
                      type="select"
                      :options="timezones"
                      v-model="timezone"
                    />
                  </div>
                </div>
                <div class="scheduler">
                  <div class="calendar">
                    <v-date-picker
                      v-model="currentDate"
                      :available-dates="availableDates"
                      :timezone="timezone"
                      v-if="timezone"
                      is-expanded
                    />
                  </div>
                  <div class="slots">
                    <div class="cards" v-if="slotsByDay">
                      <div
                        class="card"
                        :class="{
                          active: selectedSlot === slot.from + slot.to,
                        }"
                        v-for="slot in slotsByDay"
                        :key="slot.from + slot.to"
                        @click="selectSlot(slot)"
                      >
                        <h3 style="font-size: 16px; line-height: 100%">
                          {{
                            moment(`2021-01-01 ${slot.from}`).format("h:mma")
                          }}
                          to
                          {{ moment(`2021-01-01 ${slot.to}`).format("h:mma") }}
                        </h3>
                      </div>
                    </div>
                    <div
                      v-if="!slotsByDay && !loadingSlots && currentDate"
                      style="color: tomato"
                    >
                      There are no available slots
                    </div>
                  </div>
                </div>
                <FormulateForm @submit="completeStep3" style="margin-top: 40px">
                  <FormulateInput
                    type="submit"
                    :name="createTicket ? 'Go to Step 2' : 'Go to Step 4'"
                  />
                </FormulateForm>
              </div>
              <!-- Step 4: Confirm booking -->
              <div v-show="step === 4" id="step-4">
                <p v-if="from">
                  Your appointment is on
                  <strong
                    >{{
                      moment
                        .utc(from)
                        .tz(timezone)
                        .format("dddd MMMM D, YYYY")
                    }}
                    from
                    {{
                      moment
                        .utc(from)
                        .tz(timezone)
                        .format("h:mma")
                    }}
                    to
                    {{
                      moment
                        .utc(to)
                        .tz(timezone)
                        .format("h:mma z")
                    }}</strong
                  >.
                </p>
                <FormulateForm
                  @submit="confirmBooking"
                  class="flex-center"
                  style="width: 400px"
                >
                  <FormulateInput
                    type="text"
                    v-model="first_name"
                    label="First name"
                    placeholder="e.g. John"
                    validation="required"
                    error-behavior="submit"
                  />
                  <FormulateInput
                    type="text"
                    v-model="last_name"
                    label="Last name"
                    placeholder="e.g. Smith"
                    validation="required"
                    error-behavior="submit"
                  />
                  <FormulateInput
                    type="email"
                    v-model="email"
                    label="Email"
                    placeholder="e.g. example@gmail.com"
                    validation="required"
                    error-behavior="submit"
                  />
                  <FormulateInput
                    type="text"
                    v-model="phone"
                    label="Phone number"
                    placeholder="e.g. 123 456 78"
                    validation="required"
                    error-behavior="submit"
                  />
                  <FormulateInput
                    type="text"
                    v-model="company"
                    label="Company name"
                    placeholder="e.g. Acme Limited"
                    validation="required"
                    error-behavior="submit"
                  />
                  <FormulateInput
                    v-for="field in bookingFields"
                    :key="field.hubspot_property_id"
                    :type="field.type"
                    :options="
                      field.fetchOptions
                        ? apidata.metadata.options[field.hubspot_property_id]
                            .options
                        : field.options
                    "
                    v-model="hubspot_fields[field.hubspot_property_id]"
                    :label="field.label"
                    validation="required"
                    error-behavior="submit"
                  />
                  <FormulateInput
                    type="submit"
                    :name="`Complete your booking${testing ? ' TEST' : ''}`"
                  />
                </FormulateForm>
              </div>
              <!-- Final: Success -->
              <div v-if="step === 5" style="align-items: flex-start">
                <div class="completion-title">
                  <span class="material-icons">done</span>
                  <h2 style="margin: 0; padding-top: 3px; line-height: 100%">
                    Booking complete!
                  </h2>
                </div>
                <div class="completion-datetime">
                  <div style="align-items: flex-start">
                    <p>Your appointment is on</p>
                    <p>
                      <strong>
                        {{
                          moment
                            .utc(from)
                            .tz(timezone)
                            .format("dddd MMMM D, YYYY")
                        }}
                      </strong>
                    </p>
                    <p>
                      from
                      <strong>{{
                        moment
                          .utc(from)
                          .tz(timezone)
                          .format("h:mma")
                      }}</strong>
                      to
                      <strong>{{
                        moment
                          .utc(to)
                          .tz(timezone)
                          .format("h:mma")
                      }}</strong>
                    </p>
                    <p>
                      <strong
                        >{{
                          moment
                            .utc(to)
                            .tz(timezone)
                            .format("z")
                        }}
                      </strong>
                      time.
                    </p>
                  </div>
                  <div class="calendar-icon">
                    <img src="@/assets/calendar.png" alt="" />
                  </div>
                </div>
                <div
                  class="add-to-calendar"
                  style="align-items: flex-start!important;"
                >
                  <button
                    @click="openCalendarLink('ics')"
                    style="margin-bottom:10px"
                  >
                    Add to Outlook Calendar
                  </button>
                  <button
                    @click="openCalendarLink('gcal')"
                    style="margin-bottom:10px"
                  >
                    Add to Google Calendar
                  </button>
                  <button @click="openCalendarLink('ics')">
                    Add to Other Calendar
                  </button>
                </div>
                <div
                  class="completion-help"
                  style="align-items: flex-start !important; margin-top: 20px"
                >
                  <p>You will receive an email confirmation shortly</p>
                  <p>
                    If you don't receive your email in the next twenty minutes,
                    please check your junk folder. If it’s not in your junk
                    folder then please call us at
                    {{ apidata.system.booking_phone }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="[1, 2, 5].includes(step)" class="col-md-7 right-column">
            <img
              :src="apidata.system.booking_hero_url"
              alt="Booking hero image"
              style="margin-top:8px;border-radius:10px;width:100%;"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="footer">
      If you need assistance, please call {{ apidata.system.booking_phone }}
    </div>
  </div>
</template>

<script>
import { convertSlotsToTimezone } from "slot-calculator";
import moment from "moment-timezone";

export default {
  data: function() {
    return {
      moment: moment,
      ticket_id: null,
      step: 1,
      stepNames: {
        1: "Locate your details",
        2: "Confirm your details",
        3: "Select your time slot",
        4: "Confirm your appointment",
      },
      loading: false,
      loadingSlots: false,
      ticket_data: null,
      // rawSlots: {
      //   "2021-06-05": [
      //     { from: "05:00", to: "06:00", users: ["32180601"] },
      //     { from: "06:00", to: "07:00", users: ["32180601"] },
      //   ],
      // },
      rawSlots: null,
      timezone: null,
      currentDate: null,
      slotsByDay: null,
      availableDates: null,
      availableSlots: null,
      from: null,
      to: null,
      timezones: [
        {
          label: "Pacific Time",
          value: "America/Los_Angeles",
        },
        {
          label: "Mountain Time",
          value: "America/Denver",
        },
        {
          label: "Central Time",
          value: "America/Chicago",
        },
        {
          label: "Eastern Time",
          value: "America/New_York",
        },
      ],
      first_name: null,
      last_name: null,
      email: null,
      phone: null,
      company: null,
      user_contact_id: null,
      rep_contact_id: null,
      hubspot_fields: {},
      showBookedError: null,
      selectedContactId: null,
      selectedSlot: null,
      testing: null,
      gcal_link: null,
      ics_link: null,
      createTicket: false,
    };
  },

  methods: {
    getTicket: async function() {
      try {
        this.loading = true;
        const response = await this.$axios({
          method: "get",
          url: "https://en67w78x52zsxzw.m.pipedream.net",
          params: {
            ticket_id: this.ticket_id,
          },
        });

        if (
          response.data.ticket.properties.hs_pipeline !==
          this.apidata.system.ticket_pipeline_id
        ) {
          throw new Error("This Upgrade ID is for a different TAC system");
        }

        this.ticket_data = response.data;
        this.step = 2;
      } catch (error) {
        if (error.response?.data === "Request failed with status code 404") {
          this.$toast.error(
            `Please enter a valid ${this.apidata.system.booking_ticket_id_label}. If you need help locating your ID, call ${this.apidata.system.booking_phone}.`
          );
        } else {
          this.$toast.error(error.response?.data ?? error.message);
        }
      } finally {
        this.loading = false;
      }
    },
    selectContact: function(contact) {
      this.first_name = contact.properties.firstname;
      this.last_name = contact.properties.lastname;
      this.email = contact.properties.email;
      this.phone = contact.properties.phone;
      this.company = contact.properties.company;
      this.user_contact_id = contact.id;
      this.selectedContactId = contact.id;
    },
    selectNewContact: function() {
      this.first_name = null;
      this.last_name = null;
      this.email = null;
      this.phone = null;
      this.company = this.ticket_data.company.name;
      this.user_contact_id = null;
      this.selectedContactId = "none";
    },
    completeStep2: function() {
      if (this.company === null || this.company === undefined) {
        this.$toast.error("Please select an option.");
      } else {
        this.step = 3;
      }
    },
    getSlots: async function() {
      try {
        this.loadingSlots = true;
        const response = await this.$axios({
          method: "get",
          url: "https://enx906fgr59oc77.m.pipedream.net",
          params: {
            system_id: this.apidata.system.system_id,
          },
        });

        this.rawSlots = response.data.slots;
        return true;
      } catch (error) {
        this.$toast.error(error.response?.data ?? error.message);
      } finally {
        this.loadingSlots = false;
      }
    },
    manipulateSlots: function(newTimezone, oldTimezone) {
      if (!this.timezone || !this.rawSlots) return;
      const { availableSlots, availableDates } = convertSlotsToTimezone({
        slots: this.rawSlots,
        timezone: this.timezone,
      });
      this.availableDates = availableDates.map((date) =>
        moment.tz(date, this.timezone).toDate()
      );
      this.availableSlots = availableSlots;
      const currentDateString = moment
        .tz(this.currentDate, oldTimezone)
        .format("YYYY-MM-DD");
      if (oldTimezone && availableDates.includes(currentDateString)) {
        this.currentDate = moment.tz(currentDateString, this.timezone).toDate();
      } else {
        this.currentDate = moment.tz(availableDates[0], this.timezone).toDate();
      }
    },
    getSlotsByDay: function() {
      this.slotsByDay = this.availableSlots[
        moment.tz(this.currentDate, this.timezone).format("YYYY-MM-DD")
      ];
    },
    selectSlot: async function(slot) {
      const from = moment
        .tz(
          `${moment(this.currentDate).format("YYYY-MM-DD")} ${slot.from}`,
          this.timezone
        )
        .utc()
        .valueOf();

      if (
        from <
        moment()
          .add(1, "day")
          .valueOf()
      ) {
        this.$toast.error(
          "Time slot is too close. Please choose another slot."
        );
        await this.getSlots();
        this.manipulateSlots();
        return;
      }

      let toDate = moment(this.currentDate);
      if (slot.to === "00:00") {
        toDate = toDate.add(1, "day");
      }

      this.from = from;
      const to = moment
        .tz(`${toDate.format("YYYY-MM-DD")} ${slot.to}`, this.timezone)
        .utc()
        .valueOf();
      this.to = to;
      this.rep_contact_id =
        slot.users[Math.floor(Math.random() * slot.users.length)];
      this.showBookedError = false;
      this.selectedSlot = slot.from + slot.to;
    },
    completeStep3: function() {
      if (this.from) {
        this.step = 4;
      } else {
        this.$toast.error("Please select a time slot.");
      }
    },
    confirmBooking: async function() {
      try {
        this.loading = true;

        let {
          first_name,
          last_name,
          email,
          phone,
          company,
          user_contact_id,
          rep_contact_id,
          from,
          to,
          hubspot_fields,
          timezone,
          ticket_id,
        } = this;

        // Create ticket
        if (this.createTicket) {
          const response = await this.$axios({
            method: "post",
            // https://pipedream.com/@esminbound/superior-press-tac-create-ticket-p_ljCOA7g/edit
            url: "https://enmk4tidxflaakq.m.pipedream.net",
            data: {
              contact: {
                firstname: first_name,
                lastname: last_name,
                company,
                email,
              },
              ticket: {
                hs_pipeline: this.apidata.system.ticket_pipeline_id,
                hs_pipeline_stage: this.apidata.system
                  .ticket_scheduled_status_id,
                tac_customer_raised_ticket: "true",
              },
            },
          });
          ticket_id = response.data.ticket_id;
        }
        
        const booking_data = {
          first_name,
          last_name,
          email,
          phone,
          company,
          user_contact_id,
          rep_contact_id,
          from,
          to,
          hubspot_fields,
          timezone,
          ticket_id,
          portal_id: this.apidata.system.portal_id,
          system_id: this.apidata.system.system_id,
          ticket_scheduled_status_id: this.apidata.system
            .ticket_scheduled_status_id,
        };
        if (!ticket_id) throw new Error("No ticket ID found.");

        let confirmResponse;
        // Comment out if testing
        confirmResponse = await this.$axios({
          method: "post",
          // https://pipedream.com/@esminbound/superior-press-tac-confirm-booking-p_dDCb5GV/edit
          url: "https://enockhezed2836g.m.pipedream.net",
          data: {
            booking_data,
          },
        });

        this.step = 5;
        this.gcal_link = confirmResponse.data.gcal_link;
        this.ics_link = confirmResponse.data.ics_link;
      } catch (error) {
        if (error.response?.data === "Document exists") {
          this.step = 3;
          this.showBookedError = true;
          await this.getSlots();
          this.manipulateSlots();
        } else {
          this.$toast.error(error.message);
        }
      } finally {
        this.loading = false;
      }
    },
    openCalendarLink: function(type) {
      if (type === "gcal") {
        window.open(this.gcal_link ?? "https://google.com/gcal");
      } else {
        window.open(this.ics_link ?? "https://google.com/ics");
      }
    },
    selectStep: function(stepNumber) {
      if (stepNumber < this.step) this.step = stepNumber;
    },
    handleQuery: function() {
      const bank = this.$route.query.bank;
      if (bank) {
        this.hubspot_fields["tac_associated_bank"] = bank;
      }
      if (this.$route.query.createTicket) {
        this.triggerCreateTicketMode();
      }
    },
    triggerCreateTicketMode(event) {
      event?.preventDefault()
      this.createTicket = true;
      this.step = 3;
    },
  },

  created: async function() {
    this.handleQuery();
    await this.getSlots();
    this.timezone = moment.tz.guess();
    if (!this.timezones.find((element) => element.value === this.timezone)) {
      this.timezones.push({
        label: this.timezone,
        value: this.timezone,
      });
    }
  },

  watch: {
    timezone: function(newValue, oldValue) {
      this.manipulateSlots(newValue, oldValue);
    },
    currentDate: function() {
      this.getSlotsByDay();
    },
  },

  computed: {
    bookingFields: function() {
      const fields = JSON.parse(this.apidata.system.booking_fields);
      console.log(typeof fields[0]); 
      console.dir(fields);
      const array = [];
      for (const field of fields) {
        if (field.conditions) {
          for (const condition of field.conditions) {
            if (
              this.hubspot_fields[condition.hubspot_property_id] ===
              condition.equals
            ) {
              array.push(field);
            }
          }
        } else {
          array.push(field);
        }
      }
      console.log(array);
      return array;
    },
  },
};
</script>

<style>
#booking button,
#booking .formulate-input button {
  border-radius: 100px !important;
  padding: 10px 25px !important;
  font-weight: normal !important;
  font-size: 20px;
  box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.25) !important;
}

#booking .formulate-input-errors {
  text-align: center;
}

#booking .formulate-input .formulate-input-label {
  font-weight: normal !important;
  color: #666666 !important;
}

#booking input,
#booking select,
#booking textarea {
  background-color: white !important;
  box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.25) !important;
  border: 0 !important;
}

#step-1 input {
  width: 320px;
}

#step-3 select {
  padding-left: 40px;
  width: 200px;
}

#step-4 .formulate-input-wrapper {
  width: 360px;
}

#step-4 .formulate-input-element--submit {
  display: flex;
  justify-content: center;
}

#booking .card {
  background-color: white;
  box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.25) !important;
  border: 0 !important;
}

#step-2 .card {
  width: 320px;
  padding: 15px 30px !important;
}

#booking .card:hover,
#booking .card.active {
  outline: 1.5px solid rgba(0, 164, 228, 0.9) !important;
  background-color: rgba(212, 238, 249, 0.9);
}

#booking option,
#booking .formulate-input * {
  font-family: "Helvetica Neue Light";
  font-size: 18px;
  line-height: 24px;
}
</style>

<style scoped>
@font-face {
  font-family: "Helvetica Neue Light";
  font-weight: normal;
  src: url("../assets/helvetica-neue-light.woff") format("woff");
}

@font-face {
  font-family: "Helvetica Neue Bold";
  font-weight: bold;
  src: url("../assets/helvetica-neue-bold.woff") format("woff");
}

@font-face {
  font-family: "Helvetica Neue Thin";
  font-weight: bold;
  src: url("../assets/helvetica-neue-thin.woff") format("woff");
}

*:not(h2, h3, .material-icons) {
  font-family: "Helvetica Neue Light";
  font-size: 18px;
  line-height: 24px;
}

h2 {
  font-family: "Helvetica Neue Light";
  font-size: 25px;
  line-height: 40px;
  margin-bottom: 0;
}

h3 {
  font-family: "Helvetica Neue Bold";
  font-size: 25px;
  line-height: 60px;
}

#booking {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  flex-grow: 1;
}

#booking .content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

#booking .footer {
  background-color: var(--primary-color);
  padding: 10px 10px;
  flex-shrink: 0;
  margin-top: 50px;
  font-size: 22px;
  line-height: 32px;
  color: white;
  display: flex;
  justify-content: center;
  text-align: center;
}

.scheduler {
  display: flex;
  flex-direction: row !important;
  gap: 40px;
  align-items: flex-start !important;
  width: 100%;
  max-width: 600px;
}

.calendar {
  flex-grow: 1;
}

@media (max-width: 500px) {
  .scheduler {
    flex-direction: column !important;
    align-items: center !important;
  }

  .calendar {
    width: 100%;
  }
}

.scheduler .slots .card {
  padding: 18px 23px;
  width: 200px;
}

.scheduler .slots .cards {
  max-height: 500px;
  overflow-y: auto;
}

.cards::-webkit-scrollbar {
  width: 10px;
}

.cards::-webkit-scrollbar-thumb {
  background-color: var(--secondary-color);
  border-radius: 10px;
}

.cards::-webkit-scrollbar-track {
  background: white;
}

.show-booked-error {
  padding: 10px;
  border: 1px solid red;
  color: tomato;
  max-width: 600px;
  border-radius: 5px;
  margin-bottom: 10px;
}

.timezone-selector {
  display: flex;
  flex-direction: row !important;
  gap: 20px;
}

.blue-card div {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.blue-card h2,
h3,
p {
  text-align: center;
  max-width: 700px;
}

.blue-card {
  padding: 20px;
  padding-bottom: 50px;
  border-radius: 10px;
  background-color: rgba(234, 243, 246, 0.6);
  flex-grow: 1;
  max-width: 900px;
  width: 100%;
}

.step-number,
.step-tick {
  font-size: 25px !important;
  color: #808080;
  background-color: white;
  border-radius: 100px;
  width: 35px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.25) !important;
  vertical-align: middle;
  line-height: 100% !important;
  padding-top: 3px;
}

.step-number.current,
.step-tick {
  background-color: var(--primary-color);
  color: white;
}

.step-number.current {
  outline: 2px solid var(--primary-color);
  border: 1px solid white;
}

.step-icons {
  display: flex;
  flex-direction: row !important;
  border-bottom: 1.5px solid grey;
  height: 0px;
  margin-bottom: 50px;
  gap: 40px;
}

.step-clickable {
  cursor: pointer;
}

.ticket-id-container {
  display: flex;
  flex-direction: row !important;
  gap: 10px;
  align-items: center;
}

.ticket-id-question {
  background-color: var(--secondary-color);
  color: white;
  border-radius: 100px;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.25) !important;
  cursor: pointer;
}

.ticket-id-tooltip {
  max-width: 200px;
  font-size: 14px;
}

span.drop-down-arrow {
  display: block;
  position: absolute;
  pointer-events: none;
  left: 10px;
  top: 9px;
  z-index: 999;
}

.completion-title {
  display: flex;
  flex-direction: row !important;
  gap: 10px;
  align-items: center;
}

.completion-title .material-icons {
  background-color: var(--primary-color);
  border-radius: 100px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 40px;
}

.completion-datetime {
  display: flex;
  flex-direction: row !important;
  align-items: center;
  margin-top: 20px;
  justify-content: space-between;
  align-self: stretch;
}

.completion-datetime p,
.completion-help p {
  margin: 5px 0 !important;
  text-align: left !important;
}

.add-to-calendar button {
  width: 300px;
}
</style>
